import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { 
  MessageSquare, HelpCircle, Plus, Trash2, GripVertical, Save, RotateCcw,
  LayoutDashboard, FileText, Palette, Link2, Users, BarChart3, LogOut, 
  Loader2, ChevronDown, ChevronUp, Type, List, Calendar, Phone, Mail, AlignLeft
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUESTION_TYPES = [
  { value: 'text', label: 'Short Text', icon: Type },
  { value: 'textarea', label: 'Long Text', icon: AlignLeft },
  { value: 'select', label: 'Single Choice', icon: List },
  { value: 'date', label: 'Date', icon: Calendar },
  { value: 'phone', label: 'Phone Number', icon: Phone },
  { value: 'email', label: 'Email', icon: Mail }
];

const QuestionsPage = () => {
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      // Auth via httpOnly cookie - no header needed
      const response = await axios.get(`${API}/intake-questions/custom`);
      setQuestions(response.data.questions || response.data);
    } catch {
      toast.error('Failed to load questions');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/intake-questions/custom`, questions);
      toast.success('Questions saved!');
    } catch {
      toast.error('Failed to save questions');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!confirm('Reset all questions to defaults? This cannot be undone.')) return;
    
    try {
      const response = await axios.post(`${API}/intake-questions/custom/reset`);
      setQuestions(response.data.questions || response.data);
      toast.success('Questions reset to defaults');
    } catch {
      toast.error('Failed to reset questions');
    }
  };

  const addQuestion = () => {
    const newQuestion = {
      id: `q_${Date.now()}`,
      type: 'text',
      field_name: `custom_${Date.now()}`,
      question_text: 'New Question',
      placeholder: '',
      required: false,
      options: [],
      order: questions.length + 1,
      active: true
    };
    setQuestions([...questions, newQuestion]);
    setExpandedId(newQuestion.id);
  };

  const updateQuestion = (id, updates) => {
    setQuestions(questions.map(q => 
      q.id === id ? { ...q, ...updates } : q
    ));
  };

  const deleteQuestion = (id) => {
    if (!confirm('Delete this question?')) return;
    setQuestions(questions.filter(q => q.id !== id));
  };

  const moveQuestion = (fromIndex, toIndex) => {
    const newQuestions = [...questions];
    const [removed] = newQuestions.splice(fromIndex, 1);
    newQuestions.splice(toIndex, 0, removed);
    setQuestions(newQuestions.map((q, i) => ({ ...q, order: i + 1 })));
  };

  const addOption = (questionId) => {
    const question = questions.find(q => q.id === questionId);
    const newOptions = [...(question.options || []), { value: '', label: '' }];
    updateQuestion(questionId, { options: newOptions });
  };

  const updateOption = (questionId, optionIndex, field, value) => {
    const question = questions.find(q => q.id === questionId);
    const newOptions = [...question.options];
    newOptions[optionIndex] = { ...newOptions[optionIndex], [field]: value };
    // Auto-set value from label if not set
    if (field === 'label' && !newOptions[optionIndex].value) {
      newOptions[optionIndex].value = value.toLowerCase().replace(/\s+/g, '_');
    }
    updateQuestion(questionId, { options: newOptions });
  };

  const removeOption = (questionId, optionIndex) => {
    const question = questions.find(q => q.id === questionId);
    const newOptions = question.options.filter((_, i) => i !== optionIndex);
    updateQuestion(questionId, { options: newOptions });
  };

  const getTypeIcon = (type) => {
    const typeInfo = QUESTION_TYPES.find(t => t.value === type);
    return typeInfo ? typeInfo.icon : Type;
  };

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Link2, label: 'Integrations', path: '/integrations' },
    { icon: FileText, label: 'Embed Code', path: '/embed' },
    { icon: Palette, label: 'Branding', path: '/branding' },
    { icon: HelpCircle, label: 'Questions', path: '/questions', active: true },
    { icon: Users, label: 'Team', path: '/team' },
    { icon: BarChart3, label: 'Analytics', path: '/analytics' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-slate-900 text-white p-6 flex flex-col fixed h-full">
        <div className="flex items-center gap-3 mb-10">
          <div className="w-10 h-10 bg-teal-500 rounded-xl flex items-center justify-center">
            <MessageSquare className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            IntakePal
          </span>
        </div>

        <nav className="flex-1 space-y-2">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                item.active 
                  ? 'bg-teal-500/20 text-teal-400' 
                  : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="border-t border-slate-700 pt-4 mt-4">
          <div className="flex items-center gap-3 px-4 py-2 mb-2">
            <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-sm font-medium">
              {user?.full_name?.charAt(0) || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.full_name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.firm_name}</p>
            </div>
          </div>
          <button
            onClick={logout}
            className="flex items-center gap-3 px-4 py-2 text-slate-400 hover:text-white w-full transition-colors"
          >
            <LogOut className="w-5 h-5" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64 p-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 
                className="text-3xl font-bold text-slate-900"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Intake Questions
              </h1>
              <p className="text-slate-600 mt-2">
                Customize the questions your chatbot asks potential clients.
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handleReset}
                data-testid="reset-questions-btn"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset to Defaults
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-teal-500 hover:bg-teal-600"
                data-testid="save-questions-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Questions List */}
          <div className="space-y-3">
            {questions.map((question, index) => {
              const TypeIcon = getTypeIcon(question.type);
              const isExpanded = expandedId === question.id;
              
              return (
                <Card 
                  key={question.id}
                  className={`border-slate-200 ${!question.active ? 'opacity-60' : ''}`}
                  draggable
                  onDragStart={() => setDraggedIndex(index)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => {
                    if (draggedIndex !== null && draggedIndex !== index) {
                      moveQuestion(draggedIndex, index);
                    }
                    setDraggedIndex(null);
                  }}
                >
                  <CardHeader className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <div className="cursor-grab text-slate-400 hover:text-slate-600">
                        <GripVertical className="w-5 h-5" />
                      </div>
                      <div className="w-8 h-8 bg-slate-100 rounded flex items-center justify-center">
                        <TypeIcon className="w-4 h-4 text-slate-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">
                          {question.question_text}
                        </p>
                        <p className="text-xs text-slate-500">
                          {QUESTION_TYPES.find(t => t.value === question.type)?.label} 
                          {question.required && ' • Required'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={question.active}
                          onCheckedChange={(checked) => updateQuestion(question.id, { active: checked })}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setExpandedId(isExpanded ? null : question.id)}
                        >
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  
                  {isExpanded && (
                    <CardContent className="pt-0 pb-4 px-4 border-t">
                      <div className="grid gap-4 mt-4">
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div>
                            <Label>Question Type</Label>
                            <Select
                              value={question.type}
                              onValueChange={(value) => updateQuestion(question.id, { type: value })}
                            >
                              <SelectTrigger className="mt-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {QUESTION_TYPES.map(type => (
                                  <SelectItem key={type.value} value={type.value}>
                                    <div className="flex items-center gap-2">
                                      <type.icon className="w-4 h-4" />
                                      {type.label}
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Field Name (internal)</Label>
                            <Input
                              value={question.field_name}
                              onChange={(e) => updateQuestion(question.id, { field_name: e.target.value })}
                              className="mt-1"
                              placeholder="e.g., injury_location"
                            />
                          </div>
                        </div>
                        
                        <div>
                          <Label>Question Text</Label>
                          <Textarea
                            value={question.question_text}
                            onChange={(e) => updateQuestion(question.id, { question_text: e.target.value })}
                            className="mt-1"
                            rows={2}
                          />
                        </div>
                        
                        <div>
                          <Label>Placeholder Text (optional)</Label>
                          <Input
                            value={question.placeholder || ''}
                            onChange={(e) => updateQuestion(question.id, { placeholder: e.target.value })}
                            className="mt-1"
                            placeholder="e.g., Enter your answer..."
                          />
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Switch
                            id={`required-${question.id}`}
                            checked={question.required}
                            onCheckedChange={(checked) => updateQuestion(question.id, { required: checked })}
                          />
                          <Label htmlFor={`required-${question.id}`}>Required question</Label>
                        </div>
                        
                        {question.type === 'select' && (
                          <div>
                            <Label className="mb-2 block">Answer Options</Label>
                            <div className="space-y-2">
                              {(question.options || []).map((option, optIdx) => (
                                <div key={optIdx} className="flex gap-2">
                                  <Input
                                    value={option.label}
                                    onChange={(e) => updateOption(question.id, optIdx, 'label', e.target.value)}
                                    placeholder="Option label"
                                    className="flex-1"
                                  />
                                  <Input
                                    value={option.value}
                                    onChange={(e) => updateOption(question.id, optIdx, 'value', e.target.value)}
                                    placeholder="Value"
                                    className="w-32"
                                  />
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => removeOption(question.id, optIdx)}
                                    className="text-red-500 hover:text-red-600"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              ))}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => addOption(question.id)}
                              >
                                <Plus className="w-4 h-4 mr-1" />
                                Add Option
                              </Button>
                            </div>
                          </div>
                        )}
                        
                        <div className="flex justify-end pt-2 border-t">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteQuestion(question.id)}
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete Question
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  )}
                </Card>
              );
            })}
          </div>

          {/* Add Question Button */}
          <Button
            variant="outline"
            onClick={addQuestion}
            className="w-full mt-4 border-dashed"
            data-testid="add-question-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add New Question
          </Button>

          {/* Preview Info */}
          <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h3 className="font-medium text-blue-900 mb-1">Preview Your Widget</h3>
            <p className="text-sm text-blue-700">
              After saving, view your updated chatbot at{' '}
              <Link to="/chat-demo" className="underline">Chat Demo</Link> to see how your questions appear to clients.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default QuestionsPage;
